import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ArrowLeft, ArrowUp, ArrowDown, Buildings, Info } from '@phosphor-icons/react';
import api from '../../../../../../services/api';
import { financeRoutes } from '../../../../../../utils/RoutesApi';
import { Tooltip } from 'antd';
import { 
    LoadingMessage, 
    MetricCard, 
    CardsGrid, 
    ScoreContent, 
    InfoItem, 
    InfoGrid, 
    LogoPlaceholder, 
    TickerBadge,
    SummaryCard,
    Tabs,
    Header,
    Container,
    Content,
    PeriodFilters,
    CompanyAboutCard,
    CompanyAboutHeader,
    CompanyDescription,
    CompanySiteLink,
    ContentEmpresa,
} from '../StocksDetails/styles.ts';
import {
  GraphicCotationHistoryContainer,
  HeaderTopBar,
  HeaderGraphic,
  FiltersDate,
  FilterSelect,
  StyledQuoteTable,
  PercentValue,
  PercentValueCumulative,
} from '../../../../../../components/ResumoAcao/components/graphics/GraphicCotationHistory/styles';

function formatCurrency(value) {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return '-';
  return `${(value * 100).toFixed(2)}%`;
}

function formatNumber(value) {
  if (value === null || value === undefined) return '-';

  return new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value);
}

function calculateStockScore(asset) {
  const kpi = asset?.latest_kpi || asset?.kpi || asset?.kpis || {};

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const clamp = (value, min = 0, max = 100) => {
    return Math.min(Math.max(value, min), max);
  };

  const netMargin = toNumber(kpi.net_margin);
  const ebitdaMargin = toNumber(kpi.ebitda_margin);
  const dividendYield = toNumber(kpi.dy ?? kpi.avg_dy);
  const netDebt = toNumber(kpi.net_debt);
  const netDebtEbitda = toNumber(kpi.net_debt_ebitda);

  const values = [
    clamp(netMargin * 100),
    clamp(ebitdaMargin * 100),
    clamp(dividendYield * 100),
    clamp(netDebt <= 0 ? 100 : 50),
    clamp(netDebtEbitda <= 0 ? 100 : 100 - netDebtEbitda * 20),
  ];

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function MiniLineChart({ data = [], type = 'number' }) {
  const series = [
    {
      name: 'Valor',
      data: data.map((item) => item.value || 0),
    },
  ];

  const formatTooltipValue = (value) => {
    if (value === null || value === undefined) return '-';

    if (type === 'currency') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 2,
      }).format(value);
    }

    if (type === 'percent') {
      return `${(value * 100).toFixed(2)}%`;
    }

    return Number(value).toFixed(2);
  };

  const options = {
    chart: {
      type: 'area',
      sparkline: {
        enabled: true,
      },
      toolbar: {
        show: false,
      },
    },

    colors: ['#52C41A'],

    stroke: {
      curve: 'smooth',
      width: 2,
    },

    fill: {
      opacity: 0.15,
      colors: ['#52C41A'],
    },

    tooltip: {
      enabled: true,
      y: {
        formatter: formatTooltipValue,
      },
    },

    dataLabels: {
      enabled: false,
    },
  };

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="area"
      height={55}
    />
  );
}

function ScoreChart({ asset }) {
  const kpi = asset?.latest_kpi || asset?.kpi || asset?.kpis || {};

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  };

  const clamp = (value, min = 0, max = 100) => {
    return Math.min(Math.max(value, min), max);
  };

  const netMargin = toNumber(kpi.net_margin);
  const ebitdaMargin = toNumber(kpi.ebitda_margin);
  const dividendYield = toNumber(kpi.dy ?? kpi.avg_dy);
  const netDebt = toNumber(kpi.net_debt);
  const netDebtEbitda = toNumber(kpi.net_debt_ebitda);

  const values = [
    clamp(netMargin * 100),
    clamp(ebitdaMargin * 100),
    clamp(dividendYield * 100),
    clamp(netDebt <= 0 ? 100 : 50),
    clamp(netDebtEbitda <= 0 ? 100 : 100 - netDebtEbitda * 20),
  ];

  const score = calculateStockScore(asset);

  const remaining = Math.max(
    100 * values.length - values.reduce((sum, value) => sum + value, 0),
    0
  );

  const options = {
    chart: {
      type: 'donut',
    },
    stroke: {
      show: false,
    },
    legend: {
      show: false,
    },
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 90,
        donut: {
          size: '65%',
          labels: {
            show: false,
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
        y: {
          formatter: (val) => {
            if (val > 100) return '';
            return `${val.toFixed(0)}%`;
          },
        },
      },
    labels: [
      'Lucro Líquido',
      'EBITDA',
      'Dividendos',
      'Dívida Líquida',
      'Dívida Líquida EBITDA',
      '',
    ],
    colors: [
      '#38486B',
      '#635185',
      '#99538E',
      '#CD5485',
      '#F45F6A',
      '#E4E5E7',
    ],
  };

  return (
    <ScoreContent>
      <ReactApexChart
        options={options}
        series={[...values, remaining]}
        type="donut"
        height={180}
        width={220}
      />

      <Tooltip title="Nota calculada com base nos KPIs disponíveis da empresa">
        <h6>{score.toFixed(0)}%</h6>
      </Tooltip>
    </ScoreContent>
  );
}

export default function StockDetails({ ticker, onBack }) {
  const [asset, setAsset] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dividendYieldInfo, setDividendYieldInfo] = useState(null);
  const [dividendYieldHistory, setDividendYieldHistory] = useState([]);
  const [profitVsPriceModalOpen, setProfitVsPriceModalOpen] = useState(false);
  const [modalChart, setModalChart] = useState({
    isOpen: false,
    title: '',
    value: '',
    data: [],
    period: '5y',
  });

  async function debugInternationalRoutes(ticker) {
    // debugInternationalRoutes('AAPL'); // STOCK
    // debugInternationalRoutes('ABR');    // REIT
    // debugInternationalRoutes('VOO');  // ETF
    const routes = [
      {
        name: 'LISTAGEM GERAL',
        request: () => api.get(financeRoutes.INTERNATIONAL_ASSETS),
      },
      {
        name: 'DETALHE',
        request: () => api.get(financeRoutes.INTERNATIONAL_ASSET_DETAIL(ticker)),
      },
      {
        name: 'STATEMENTS',
        request: () => api.get(financeRoutes.INTERNATIONAL_ASSET_STATEMENTS(ticker)),
      },
      {
        name: 'KPIS',
        request: () => api.get(financeRoutes.INTERNATIONAL_ASSET_KPIS(ticker)),
      },
      {
        name: 'HOLDINGS',
        request: () => api.get(financeRoutes.INTERNATIONAL_ASSET_HOLDINGS(ticker)),
      },
      {
        name: 'PRICE HISTORY',
        request: () =>
          api.get(financeRoutes.INTERNATIONAL_ASSET_PRICE_HISTORY(ticker), {
            params: { period: '10y' },
          }),
      },
      {
        name: 'DIVIDEND YIELD',
        request: () => api.get(financeRoutes.INTERNATIONAL_ASSET_DIVIDEND_YIELD(ticker)),
      },
      {
        name: 'POSITIONS',
        request: () => api.get(financeRoutes.INTERNATIONAL_POSITIONS),
      },
    ];

    console.group(`DEBUG ROTAS INTERNACIONAIS - ${ticker}`);

    const results = await Promise.allSettled(
      routes.map(async (route) => {
        try {
          const response = await route.request();

          return {
            rota: route.name,
            status: response.status,
            data: response.data,
          };
        } catch (error) {
          return {
            rota: route.name,
            status: error?.response?.status || 'ERRO',
            data: error?.response?.data || error.message,
          };
        }
      })
    );

    results.forEach((result) => {
      const item = result.value;

      console.group(`${item.rota} - STATUS ${item.status}`);
      console.log(item.data);
      console.groupEnd();
    });

    console.groupEnd();

    return results.map((result) => result.value);
  }

  useEffect(() => {
    async function loadStockDetails() {
      try {
        setLoading(true);

        const [assetResponse, kpisResponse, priceResponse, dividendYieldResponse] = await Promise.all([
          api.get(financeRoutes.INTERNATIONAL_ASSET_DETAIL(ticker)),
          api.get(financeRoutes.INTERNATIONAL_ASSET_KPIS(ticker)),
          api.get(financeRoutes.INTERNATIONAL_ASSET_PRICE_HISTORY(ticker), {
            params: {
              period: '10y',
            },
          }),
          api.get(financeRoutes.INTERNATIONAL_ASSET_DIVIDEND_YIELD(ticker)),
        ]);

        setAsset(assetResponse.data);
        setKpis(kpisResponse.data || []);
        setPriceHistory(priceResponse.data || []);
        const dividendYieldData = dividendYieldResponse.data;

        setDividendYieldInfo(dividendYieldData);

        setDividendYieldHistory(
          Array.isArray(dividendYieldData?.series)
            ? dividendYieldData.series
            : []
        );
      } catch (error) {
        console.error(`Erro ao buscar detalhes de ${ticker}:`, error);
      } finally {
        setLoading(false);
      }
    }

    if (ticker) {
      loadStockDetails();

      if (process.env.NODE_ENV === 'development') {
        window.debugInternationalRoutes = debugInternationalRoutes;
      }
    }
  }, [ticker]);

  if (loading) {
    return <LoadingMessage>Carregando detalhes de {ticker}...</LoadingMessage>;
  }

  if (!asset) {
    return <LoadingMessage>Não encontramos detalhes para {ticker}.</LoadingMessage>;
  }

  const latestKpi = asset.latest_kpi || {};
  const netIncomeSeries = kpis.map((item) => ({
    date: item.period_end,
    value: item.net_income,
  })).reverse();

  const ebitdaSeries = kpis.map((item) => ({
    date: item.period_end,
    value: item.ebitda,
  })).reverse();

  const netDebtSeries = kpis.map((item) => ({
    date: item.period_end,
    value: item.net_debt,
  })).reverse();

  const debtEbitdaSeries = kpis.map((item) => ({
    date: item.period_end,
    value: item.net_debt_ebitda,
  })).reverse();

  const lastPrice = priceHistory.length
    ? priceHistory[priceHistory.length - 1]?.value
    : null;

  function filterDataByPeriod(data = [], period = 'max') {
    if (period === 'max') return data;

    const limitDate = new Date();

    const periodMap = {
      '1m': { months: 1 },
      '3m': { months: 3 },
      '6m': { months: 6 },
      '1y': { years: 1 },
      '5y': { years: 5 },
      '10y': { years: 10 },
    };

    const selectedPeriod = periodMap[period];

    if (!selectedPeriod) return data;

    if (selectedPeriod.months) {
      limitDate.setMonth(limitDate.getMonth() - selectedPeriod.months);
    }

    if (selectedPeriod.years) {
      limitDate.setFullYear(limitDate.getFullYear() - selectedPeriod.years);
    }

    return data.filter((item) => {
      if (!item.date) return false;

      return new Date(item.date) >= limitDate;
    });
  }

  const filteredData = filterDataByPeriod(modalChart.data, modalChart.period);
  function calculateAccumulatedReturns(data = []) {
    const sortedData = [...data]
      .filter((item) => item.date && item.value)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const current = sortedData[sortedData.length - 1];

    if (!current) {
      return {
        oneMonth: 0,
        threeMonths: 0,
        sixMonths: 0,
        oneYear: 0,
        fiveYears: 0,
        tenYears: 0,
      };
    }

    const currentDate = new Date(current.date);
    const currentValue = Number(current.value || 0);

    function getReturnByMonths(months) {
      const targetDate = new Date(currentDate);
      targetDate.setMonth(targetDate.getMonth() - months);

      const closestItem = sortedData.find(
        (item) => new Date(item.date) >= targetDate
      );

      if (!closestItem?.value || !currentValue) return 0;

      return ((currentValue - Number(closestItem.value)) / Number(closestItem.value)) * 100;
    }

    return {
      oneMonth: getReturnByMonths(1),
      threeMonths: getReturnByMonths(3),
      sixMonths: getReturnByMonths(6),
      oneYear: getReturnByMonths(12),
      fiveYears: getReturnByMonths(60),
      tenYears: getReturnByMonths(120),
    };
  }
  function ChartModal({ modalChart, setModalChart }) {
    if (!modalChart.isOpen) return null;

    const series = [
      {
        name: modalChart.title,
        data: filteredData.map((item) => item.value || 0),
      },
    ];

    const isPriceHistory = modalChart.title === 'Histórico de cotação';
    const accumulatedReturns = calculateAccumulatedReturns(modalChart.data);
    const latestItem = filteredData[filteredData.length - 1];

    function PercentBadge({ value }) {
      const isPositive = value > 0;

      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            background: isPositive ? '#dcfce7' : '#fee2e2',
            color: isPositive ? '#16a34a' : '#ef4444',
            fontSize: '0.8rem',
            fontWeight: 600,
          }}
        >
          {isPositive ? '↑' : '↓'} {value.toFixed(2)}%
        </span>
      );
    }

    const options = {
      chart: {
        type: 'area',
        toolbar: {
          show: true,
        },
      },
      xaxis: {
        categories: filteredData.map((item) => item.date),
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      fill: {
        opacity: 0.25,
      },
      dataLabels: {
        enabled: false,
      },
    };

    return (
      <div
        onClick={() => setModalChart({ isOpen: false, title: '', value: '', data: [] })}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '2rem 1rem',
          overflowY: 'auto',
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: '80%',
            maxWidth: '90vw',
            maxHeight: '90vh',
            background: '#fff',
            borderRadius: '12px',
            padding: '2rem',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <button
            type="button"
            onClick={() => setModalChart({ isOpen: false, title: '', value: '', data: [] })}
            style={{
              float: 'right',
              border: 'none',
              background: 'transparent',
              fontSize: '1.5rem',
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            {isPriceHistory ? 'Cotação histórica' : modalChart.title}
          </h2>

          {isPriceHistory ? (
            <GraphicCotationHistoryContainer>
              <HeaderTopBar>
                <HeaderGraphic>
                  <div className="fechamento">
                    <p>
                      Fechamento de{' '}
                      <b>
                        {latestItem?.date
                          ? new Date(latestItem.date).toLocaleDateString('pt-BR')
                          : '-'}
                      </b>
                    </p>

                    <h1>
                      {latestItem?.value ? formatCurrency(latestItem.value) : '-'}
                    </h1>

                    <PercentValue upValue={accumulatedReturns.sixMonths > 0}>
                      <p className="text-variation">
                        *Variação
                        <br />
                        da <b style={{ borderBottom: 'none' }}>Cotação</b>
                      </p>

                      {accumulatedReturns.sixMonths > 0 ? <ArrowUp /> : <ArrowDown />}

                      {accumulatedReturns.sixMonths.toFixed(2)}%
                    </PercentValue>
                  </div>

                  <FiltersDate>
                    {[
                      { label: '1 Mês', value: '1m' },
                      { label: '3 Meses', value: '3m' },
                      { label: '6 Meses', value: '6m' },
                      { label: '1 Ano', value: '1y' },
                      { label: '5 Anos', value: '5y' },
                      { label: '10 Anos', value: '10y' },
                      { label: 'Máx', value: 'max' },
                    ].map((period) => (
                      <FilterSelect
                        key={period.value}
                        type="button"
                        isSelected={modalChart.period === period.value}
                        onClick={() =>
                          setModalChart((prev) => ({
                            ...prev,
                            period: period.value,
                          }))
                        }
                      >
                        {period.label}
                      </FilterSelect>
                    ))}
                  </FiltersDate>
                </HeaderGraphic>

                <StyledQuoteTable>
                  <table>
                    <thead>
                      <tr>
                        <th>Percentual acumulado</th>
                        <th>Período</th>
                      </tr>
                    </thead>

                    <tbody>
                      {[
                        ['1 mês', accumulatedReturns.oneMonth],
                        ['3 meses', accumulatedReturns.threeMonths],
                        ['6 meses', accumulatedReturns.sixMonths],
                        ['12 meses', accumulatedReturns.oneYear],
                        ['5 anos', accumulatedReturns.fiveYears],
                        ['10 anos', accumulatedReturns.tenYears],
                      ].map(([label, value]) => (
                        <tr key={label}>
                          <td>
                            <PercentValueCumulative upValue={value > 0}>
                              {value > 0 ? <ArrowUp /> : <ArrowDown />}
                              {value.toFixed(2)}%
                            </PercentValueCumulative>
                          </td>

                          <td>{label}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </StyledQuoteTable>
              </HeaderTopBar>
            </GraphicCotationHistoryContainer>
          ) : (
            <h1 style={{ textAlign: 'center', marginBottom: '2rem' }}>
              {modalChart.value}
            </h1>
          )}

          {/* {!isPriceHistory && (
            <PeriodFilters>
              {[
                { label: '1 Mês', value: '1m' },
                { label: '3 Meses', value: '3m' },
                { label: '6 Meses', value: '6m' },
                { label: '1 Ano', value: '1y' },
                { label: '5 Anos', value: '5y' },
                { label: '10 Anos', value: '10y' },
                { label: 'Máx', value: 'max' },
              ].map((period) => (
                <button
                  key={period.value}
                  type="button"
                  className={modalChart.period === period.value ? 'active' : ''}
                  onClick={() =>
                    setModalChart((prev) => ({
                      ...prev,
                      period: period.value,
                    }))
                  }
                >
                  {period.label}
                </button>
              ))}
            </PeriodFilters>
          )} */}

          <ReactApexChart
            options={options}
            series={series}
            type="area"
            height={320}
          />
        </div>
      </div>
    );
  }
  const dividendYieldSeries = dividendYieldHistory
    .map((item) => ({
      date: String(item.year),
      value:
        dividendYieldInfo?.current_price > 0
          ? item.dividends_per_share / dividendYieldInfo.current_price
          : 0,
    }))
    .filter((item) => item.date && item.value > 0);

  const latestDividendYield = dividendYieldInfo?.current_yield ?? null;

  const profitVsPriceSeries = kpis
    .map((item) => {
      const year = new Date(item.period_end).getFullYear();

      const priceItem = priceHistory.find((price) => {
        const priceYear = new Date(price.date).getFullYear();
        return priceYear === year;
      });

      return {
        year,
        netIncome: Number(item.net_income || 0),
        price: Number(priceItem?.value || 0),
      };
    })
    .filter((item) => item.year && item.netIncome > 0 && item.price > 0)
    .reverse();
  function ProfitVsPriceModal({ isOpen, onClose }) {
    if (!isOpen) return null;

    const series = [
      {
        name: asset.ticker,
        type: 'line',
        data: profitVsPriceSeries.map((item) => item.price),
      },
      {
        name: 'Lucro Líquido',
        type: 'column',
        data: profitVsPriceSeries.map((item) => item.netIncome),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        toolbar: {
          show: true,
        },
      },
      stroke: {
        width: [3, 0],
        curve: 'smooth',
      },
      plotOptions: {
        bar: {
          columnWidth: '45%',
          borderRadius: 4,
        },
      },
      dataLabels: {
        enabled: false,
      },
      labels: profitVsPriceSeries.map((item) => String(item.year)),
      xaxis: {
        type: 'category',
      },
      yaxis: [
        {
          title: {
            text: 'Cotação',
          },
          labels: {
            formatter: (value) => formatCurrency(value),
          },
        },
        {
          opposite: true,
          title: {
            text: 'Lucro Líquido',
          },
          labels: {
            formatter: (value) => formatCurrency(value),
          },
        },
      ],
      tooltip: {
        shared: true,
        y: {
          formatter: (value) => formatCurrency(value),
        },
      },
      colors: ['#38486B', '#ff7a45'],
    };

    return (
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: '780px',
            maxWidth: '92vw',
            background: '#fff',
            borderRadius: '12px',
            padding: '2rem',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              float: 'right',
              border: 'none',
              background: 'transparent',
              fontSize: '1.5rem',
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>
            Lucro x Cotação
          </h2>

          <ReactApexChart
            options={options}
            series={series}
            type="line"
            height={360}
          />
        </div>
      </div>
    );
  }
  return (
    <Container>
      <Content>
        <Header>
          <button type="button" onClick={onBack}>
            <ArrowLeft size={20} />
            {asset.name}
          </button>
          <Tabs>
            <button onClick={() => setProfitVsPriceModalOpen(true)} >Lucro x Cotação</button>
            {asset.website && (
              <button
                type="button"
                onClick={() => {
                  const url = asset.website.startsWith('http')
                    ? asset.website
                    : `https://${asset.website}`;

                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                Acessar site da empresa
              </button>
            )}
          </Tabs>
        </Header>

        {/* <Tabs>
          <button>Sobre {asset.ticker}</button>
          
          <button>Indicadores</button>
          <button>SEC Filings</button>
          <button>Relações com investidores</button>
          <button>Site</button>
        </Tabs> */}

        <SummaryCard>
          <div>
            <Tooltip title={`${formatCurrency(lastPrice)}`}>
              <TickerBadge>{asset.ticker}</TickerBadge>
            </Tooltip>

            <LogoPlaceholder>
              <Buildings size={56} weight="fill" />
            </LogoPlaceholder>
          </div>

          <InfoGrid>
            <InfoItem>
              <span>Nome da Empresa:</span>
              <strong>{asset.name || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Ticker:</span>
              <strong>{asset.ticker || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Tipo de ativo:</span>
              <strong>{asset.asset_type || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Setor:</span>
              <strong>{asset.sector || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Bolsa:</span>
              <strong>{asset.exchange || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Moeda:</span>
              <strong>{asset.currency || '-'}</strong>
            </InfoItem>

            {/* <InfoItem>
              <span>Nome da Empresa:</span>
              <strong>{asset.name || '-'}</strong>
            </InfoItem> */}

            {/* <InfoItem>
              <span>Tipo de ativo:</span>
              <strong>{asset.asset_type || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Setor:</span>
              <strong>{asset.sector || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>indústria:</span>
              <strong>{asset.asset_type || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>País de Origem:</span>
              <strong>{asset.currency || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Ano de estreia na bolsa:</span>
              <strong>{asset.currency || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Número de funcionários:</span>
              <strong>{latestKpi.period_end || '-'}</strong>
            </InfoItem>

            <InfoItem>
              <span>Ano de fundação:</span>
              <strong>{asset.filings_count ?? '-'}</strong>
            </InfoItem> */}
          </InfoGrid>

          <ScoreChart asset={asset} />
        </SummaryCard>
      </Content>

      <CardsGrid>
        <MetricCard
          onClick={() =>
            setModalChart({
              isOpen: true,
              title: 'Lucro Líquido',
              value: formatCurrency(latestKpi.net_income),
              data: netIncomeSeries,
              period: 'max',
            })
          }
        >
          <header>
            Lucro Líquido
            <Tooltip title="Receita do último período disponível">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>
          <h3>{formatCurrency(latestKpi.net_income)}</h3>
          <MiniLineChart
            data={netIncomeSeries}
            type="currency"
          />
        </MetricCard>

        <MetricCard
          onClick={() =>
            setModalChart({
              isOpen: true,
              title: 'EBITDA',
              value: formatCurrency(latestKpi.ebitda),
              data: ebitdaSeries,
            })
          }
        >
          <header>
            EBITDA
            <Tooltip title="EBITDA do último período disponível">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>
          <h3>{formatCurrency(latestKpi.ebitda)}</h3>
          <MiniLineChart
            data={ebitdaSeries}
            type="currency"
          />
        </MetricCard>

        <MetricCard
          onClick={() =>
            setModalChart({
              isOpen: true,
              title: 'Dívida Líquida',
              value: formatCurrency(latestKpi.net_debt),
              data: netDebtSeries,
              period: 'max',
            })
          }
        >
          <header>
            Dívida Líquida
            <Tooltip title="Dívida líquida do último período disponível">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>
          <h3>{formatCurrency(latestKpi.net_debt)}</h3>
          <MiniLineChart
            data={netDebtSeries}
            type="currency"
          />
        </MetricCard>

        <MetricCard
          onClick={() =>
            setModalChart({
              isOpen: true,
              title: 'DL/EBITDA',
              value: formatNumber(latestKpi.net_debt_ebitda),
              data: debtEbitdaSeries,
              period: 'max',
            })
          }
        >
          <header>
            DL/EBITDA
            <Tooltip title="Dívida líquida dividida pelo EBITDA">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>
          <h3>{formatNumber(latestKpi.net_debt_ebitda)}</h3>
          <MiniLineChart
            data={debtEbitdaSeries}
            type="number"
          />
        </MetricCard>

        <MetricCard
          onClick={() =>
            setModalChart({
              isOpen: true,
              title: 'Dividend Yield',
              value: formatPercent(latestDividendYield),
              data: dividendYieldSeries,
              period: 'max',
            })
          }
        >
          <header>
            Dividend Yield
            <Tooltip title="Histórico de Dividend Yield vindo da rota própria do ativo">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>

          <h3>{formatPercent(latestDividendYield)}</h3>
          <MiniLineChart
            data={dividendYieldSeries}
            type="percent"
          />
        </MetricCard>

        <MetricCard
          onClick={() =>
            setModalChart({
              isOpen: true,
              title: 'Histórico de cotação',
              value: priceHistory.length
                ? formatCurrency(priceHistory[priceHistory.length - 1]?.value)
                : '-',
              data: priceHistory,
              period: '6m',
            })
          }
        >
          <header>
            Histórico de cotação
            <Tooltip title="Histórico de preço do ativo">
              <Info size={14} color="#ec6643" />
            </Tooltip>
          </header>
          <h3>
            {priceHistory.length
              ? formatCurrency(priceHistory[priceHistory.length - 1]?.value)
              : '-'}
          </h3>
          <MiniLineChart
            data={priceHistory}
            type="currency"
          />
        </MetricCard>
      </CardsGrid>
      <ContentEmpresa>
        <CompanyAboutCard>
          <CompanyAboutHeader>
            <div>
              <span>
                {asset.asset_type === 'REIT'
                  ? 'Sobre o REIT'
                  : asset.asset_type === 'STOCK'
                  ? 'Sobre o stock'
                  : 'Sobre o Ativo'}
              </span>
              <h2>{asset.name || '-'}</h2>
            </div>

            {/* {asset.website && (
              <CompanySiteLink
                href={asset.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                Acessar site da empresa
              </CompanySiteLink>
            )} */}
          </CompanyAboutHeader>

          <CompanyDescription>
            {asset.description ||
              'Ainda não temos uma descrição cadastrada para esta empresa.'}
          </CompanyDescription>

        </CompanyAboutCard>
      </ContentEmpresa>
      <ChartModal modalChart={modalChart} setModalChart={setModalChart} />
      <ProfitVsPriceModal
        isOpen={profitVsPriceModalOpen}
        onClose={() => setProfitVsPriceModalOpen(false)}
      />
    </Container>
  );
}